import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { ServiceList } from './ServiceList';
import { AboutPerson, AboutSection, TeamSection } from './TeamSection';
import { HoursCard } from './HoursCard';
import { ContactCard, hasContact } from './ContactCard';
import { LocationSection } from './LocationSection';
import type { ProfileDisplayOptions, PublicOrganization, PublicService } from '../types';

interface ProfileViewProps {
  organization: PublicOrganization;
  services: PublicService[];
  options: ProfileDisplayOptions;
  onBook: (service: PublicService) => void;
}

const SERVICES_ID = 'services';

// The profile content below the business header (step 0). Top bar, photos,
// header and footer come from BookingPageFrame and stay mounted when the
// visitor moves on to the booking steps.
export function ProfileView({ organization, services, options, onBook }: ProfileViewProps) {
  const { t } = useTranslation('bookingPage');
  const page = organization.booking_page;
  const photosShown = options.photos && page.layout !== 'none' && page.photos.length > 0;
  const soloMember = !page.show_team && page.team.length === 1 ? page.team[0] : null;
  // The sidebar holds the Opening hours and Contact cards; with neither (or
  // when the embed turns the sidebar off) the page is one column.
  const hasInfo = !!page.hours || hasContact(organization);
  const withRail = options.sidebar && hasInfo;

  return (
    <div
      className={cn('grid gap-10', withRail && 'lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-12')}
      data-testid="booking-profile"
      data-layout={photosShown ? page.layout : 'none'}
    >
      <div className="flex min-w-0 flex-col gap-10 sm:gap-12">
        <section id={SERVICES_ID} className="flex scroll-mt-4 flex-col gap-4">
          <div className="flex items-baseline justify-between">
            <h2 className="font-display text-[20px] font-semibold tracking-tight text-ink sm:text-[24px]">{t('services.title')}</h2>
            <span className="text-body text-soft">{t('services.count', { count: services.length })}</span>
          </div>
          <ServiceList services={services} onBook={onBook} />
        </section>

        {page.show_team && page.team.length > 0 && <TeamSection team={page.team} />}
        {soloMember && <AboutPerson member={soloMember} about={page.about} />}
        {!soloMember && page.about && <AboutSection about={page.about} />}

        {hasInfo && (
          <section className={cn('flex flex-col gap-3', withRail && 'lg:hidden')} data-testid="hours-contact-section">
            <h2 className="font-display text-[20px] font-semibold tracking-tight text-ink sm:text-[24px]">{page.hours ? t('hours.section') : t('contact.title')}</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <HoursCard organization={organization} />
              <ContactCard organization={organization} />
            </div>
          </section>
        )}

        {page.location && <LocationSection location={page.location} />}
      </div>

      {withRail && (
        <aside className="hidden flex-col gap-4 lg:sticky lg:top-6 lg:flex lg:self-start" data-testid="info-rail">
          <HoursCard organization={organization} elevated />
          <ContactCard organization={organization} elevated />
        </aside>
      )}
    </div>
  );
}
